import { useState, useEffect, useCallback } from "react";
import { X, Info, AlertTriangle, AlertOctagon } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "gm_dismissed_version";

const SEVERITY = {
  info: {
    wrap: "bg-blue-600 text-white",
    icon: Info,
  },
  warning: {
    wrap: "bg-amber-500 text-slate-900",
    icon: AlertTriangle,
  },
  critical: {
    wrap: "bg-red-600 text-white",
    icon: AlertOctagon,
  },
};

// Single global announcement banner, shown on every route (login + in-app).
// Admin-controlled via Env tab. Dismissal is stored in localStorage keyed to the
// message version, so a user dismisses it ONCE per browser and it only reappears
// when the admin edits the text/severity (version changes).
export default function GlobalMessageBanner() {
  // Never render inside the embeddable B2B widget (iframe/embed context).
  const isWidget = typeof window !== "undefined" && window.location.pathname.startsWith("/widget");

  const [data, setData] = useState(null);
  const [dismissedVersion, setDismissedVersion] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) || ""; } catch { return ""; }
  });

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/quickpick/global-message`);
      if (r.ok) setData(await r.json());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (isWidget) return;
    load();
    // Re-check on tab focus so a freshly-published message shows up.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load, isWidget]);

  if (isWidget) return null;
  if (!data?.show || !data.version) return null;
  if (dismissedVersion === data.version) return null;

  const sev = SEVERITY[data.severity] || SEVERITY.info;
  const Icon = sev.icon;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, data.version); } catch { /* ignore */ }
    setDismissedVersion(data.version);
  };

  return (
    <div
      className={`w-full ${sev.wrap} px-4 py-2.5 flex items-center gap-3 text-sm font-medium shadow-md`}
      data-testid="global-message-banner"
      role="alert"
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="flex-1 whitespace-pre-wrap" data-testid="global-message-text">{data.message}</span>
      <button
        onClick={dismiss}
        className="shrink-0 inline-flex items-center justify-center h-6 w-6 rounded hover:bg-black/15 transition"
        aria-label="Dismiss message"
        data-testid="global-message-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
